import { DossierOmschrijvingen, Scenario, ScenarioStap } from "@/app/pages/beheer/scenarios/types.ts";
import {
    useGetDossierDetailsBySpoorwebId,
    useGetOngewensteGebeurtenissenDetails,
    usePostOngewensteGebeurtenis,
    usePostTVSM,
    useScenarioStore,
} from "@/entities/ongewenste-gebeurtenis/debug.ts";
import { Button, Empty, EmptyDescription, EmptyHeader, Icon, Separated } from "@/shared/components";
import { PrognoseType } from "@/shared/types";
import { adjustOngewensteGebeurtenisTimesAndMeldingId, adjustTvsmDossierId } from "./utils";

type ScenarioAfhandelingProps = {
    scenarioNummer: number;
    scenario: Scenario;
};

export function ScenarioAfhandeling({ scenario, scenarioNummer }: ScenarioAfhandelingProps) {
    const spoorwebIdsByScenario = useScenarioStore((s) => s.scenarios);
    const postOngewensteGebeurtenis = usePostOngewensteGebeurtenis();
    const postTvsm = usePostTVSM();

    const spoorwebIdsByScenarios = spoorwebIdsByScenario[scenario.id] ?? [];

    const results = useGetDossierDetailsBySpoorwebId(spoorwebIdsByScenarios);

    const nextOgForScenario = (scenarioStap: ScenarioStap, ogId: string, prognoses?: PrognoseType[]) => {
        const [og, logistiekeAfhandelingId] = adjustOngewensteGebeurtenisTimesAndMeldingId(scenarioStap, prognoses, ogId);
        postOngewensteGebeurtenis.mutate(og);
        if (scenarioStap.vsm) {
            const tvsm = adjustTvsmDossierId(scenarioStap.vsm, logistiekeAfhandelingId);
            postTvsm.mutate(tvsm);
        }
    };
    const { data } = useGetOngewensteGebeurtenissenDetails();
    const getCurrentOg = (ogId: string) => {
        return data?.find((og) => og.id === ogId);
    };
    const getOmschrijvingenForOg = (ogId: string): DossierOmschrijvingen | null => {
        return results?.find((details) => details?.forOgId === ogId) ?? null;
    };

    if (!spoorwebIdsByScenarios?.length) {
        return (
            <Empty>
                <EmptyHeader>
                    <EmptyDescription className="text-s text-content-subtle ml-4">
                        Nog geen ongewenste gebeurtenis op basis van scenario {scenarioNummer}
                    </EmptyDescription>
                </EmptyHeader>
            </Empty>
        );
    }

    const getHuidigeStap = (ogId: string) => scenario.stappen.find((stap) => stap.volgnummer == getCurrentOg(ogId)?.volgnummer);
    const getVolgendeStap = (ogId: string) => {
        const thisStep = scenario.stappen.findIndex((stap) => stap.volgnummer == getCurrentOg(ogId)?.volgnummer);
        if (thisStep + 1 < scenario.stappen.length) {
            return scenario.stappen[thisStep + 1];
        }
        return undefined;
    };

    return (
        <div className="grid gap-4">
            <Separated>
                {spoorwebIdsByScenarios?.map((ogId) => {
                    const currentStap = getHuidigeStap(ogId);
                    const nextStap = getVolgendeStap(ogId);
                    const currentOg = getCurrentOg(ogId);
                    const omschrijvingen = getOmschrijvingenForOg(ogId);

                    return (
                        <div className="flex flex-row items-center justify-between gap-2">
                            <div className="flex flex-row gap-2">
                                <Icon name="page-templates" />
                                <div className="grid gap-1">
                                    <span>
                                        {omschrijvingen?.omschrijving ?? "Nog geen dossier aangemaakt"}
                                        {omschrijvingen?.omschrijvingExtraContext && ` | ${omschrijvingen?.omschrijvingExtraContext}`}
                                    </span>
                                    <span className="text-s text-content-subtle font-normal">{ogId}</span>
                                </div>
                            </div>
                            <div className="flex flex-row gap-2">
                                <div className="border-default grid justify-items-end gap-2 border-r pr-2">
                                    <span className="text-s text-content-subtle font-normal">
                                        Huidige volgnummer: {currentStap?.volgnummer}: {currentStap?.beschrijving}
                                    </span>
                                    {nextStap && (
                                        <span className="text-s text-content-default font-normal">
                                            Volgende volgnummer: {nextStap.volgnummer}: {nextStap.beschrijving}
                                        </span>
                                    )}
                                </div>
                                <Button
                                    variant="tertiary"
                                    icon="send"
                                    disabled={!nextStap}
                                    onClick={() => nextOgForScenario(nextStap!, ogId, currentOg?.prognoses)}
                                >
                                    Volgende stap
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </Separated>
        </div>
    );
}
